import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Hexagon, Sparkles, HeartPulse, Wrench, Droplets, Crown, MapPin, MessageCircle, ChevronRight,
  Search, Pin, Lock, ArrowLeft, Plus, ArrowUp, ArrowDown, CheckCircle2, Trash2, Send, Award,
} from "lucide-react";
import VideoUploadField from "@/components/VideoUploadField";
import VideoLightbox from "@/components/VideoLightbox";

const pub = axios.create({ baseURL: API });

const CAT_ICONS = { Hexagon, Sparkles, HeartPulse, Wrench, Droplets, Crown, MapPin };

const formatRel = (iso) => {
  if (!iso) return "";
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d`;
  return new Date(iso).toLocaleDateString();
};

// ════════════════════════════ Layout wrapper ════════════════════════════

function ForumLayout({ children, isAuth }) {
  return (
    <div className="min-h-screen bg-stone-50" data-testid="forum-layout">
      <header className="border-b border-stone-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to={isAuth ? "/app" : "/"} className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-yellow-400 grid place-items-center">
              <Hexagon className="w-4 h-4 text-amber-900" strokeWidth={2.5} />
            </div>
            <span className="font-display text-sm font-semibold">Hive <span className="text-yellow-500">@</span> Buzzzillion · Community</span>
          </Link>
          {!isAuth && (
            <div className="flex items-center gap-2">
              <Link to="/login"><Button variant="ghost" size="sm" className="rounded-full" data-testid="forum-signin-link">Sign in</Button></Link>
              <Link to="/register"><Button size="sm" className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="forum-signup-link">Join</Button></Link>
            </div>
          )}
          {isAuth && (
            <Link to="/app" className="text-sm text-stone-500 hover:text-stone-900">← Back to app</Link>
          )}
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-10">{children}</main>
    </div>
  );
}

// ════════════════════════════ Forum index ════════════════════════════

export default function Forum() {
  const { user } = useAuth();
  const [cats, setCats] = useState(null);
  const [threads, setThreads] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    pub.get("/public/forum/categories").then((r) => setCats(r.data));
    pub.get("/public/forum/threads?limit=20").then((r) => setThreads(r.data));
  }, []);

  const search = (e) => {
    e?.preventDefault();
    pub.get(`/public/forum/threads?q=${encodeURIComponent(q)}`).then((r) => setThreads(r.data));
  };

  return (
    <ForumLayout isAuth={!!user}>
      <div className="flex items-end justify-between gap-3 mb-8 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-700">Community Forum</div>
          <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tight mt-2">Beekeepers helping beekeepers</h1>
          <p className="text-stone-600 mt-2 max-w-2xl">Browse without signing in. Sign in to post, reply, and vote.</p>
        </div>
        {user && (
          <Link to="/forum/new"><Button className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="forum-new-thread"><Plus className="w-4 h-4 mr-1.5" /> New thread</Button></Link>
        )}
      </div>

      <form onSubmit={search} className="relative mb-8" data-testid="forum-search-form">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search threads…" maxLength={120} className="pl-9" data-testid="forum-search-input" />
      </form>

      <section className="mb-12">
        <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500 mb-3">Categories</h2>
        {!cats ? <p className="text-sm text-stone-500">Loading…</p> : (
          <ul className="grid sm:grid-cols-2 gap-3">
            {cats.map((c) => {
              const Icon = CAT_ICONS[c.icon] || Hexagon;
              return (
                <li key={c.id}>
                  <Link to={`/forum/c/${c.slug}`} className="flex items-start gap-3 bg-white border border-stone-200 hover:border-amber-300 rounded-2xl p-4 transition-colors" data-testid={`forum-cat-${c.slug}`}>
                    <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-700 grid place-items-center shrink-0"><Icon className="w-4 h-4" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 justify-between">
                        <span className="font-medium">{c.name}</span>
                        <span className="text-xs text-stone-500">{c.thread_count} thread{c.thread_count === 1 ? "" : "s"}</span>
                      </div>
                      <p className="text-xs text-stone-500 mt-1">{c.description}</p>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section>
        <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500 mb-3">
          {q ? `Search results · "${q}"` : "Recent threads"}
        </h2>
        <ThreadList threads={threads} />
      </section>
    </ForumLayout>
  );
}

function ThreadList({ threads }) {
  if (threads.length === 0) return <p className="text-sm text-stone-500 py-6 text-center">No threads yet.</p>;
  return (
    <ul className="space-y-2" data-testid="thread-list">
      {threads.map((t) => (
        <li key={t.id}>
          <Link to={`/forum/t/${t.slug}`} className="flex items-center gap-3 bg-white border border-stone-200 hover:border-amber-300 rounded-xl p-4 transition-colors" data-testid={`thread-row-${t.slug}`}>
            {t.author_avatar ? <img src={t.author_avatar} alt="" className="w-9 h-9 rounded-full object-cover border border-stone-200 shrink-0" /> : (
              <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-800 grid place-items-center text-sm font-semibold shrink-0">{(t.author_name || "?")[0]?.toUpperCase()}</div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                {t.pinned && <Pin className="w-3.5 h-3.5 text-amber-700 shrink-0" />}
                {t.locked && <Lock className="w-3.5 h-3.5 text-stone-400 shrink-0" />}
                {t.solved_post_id && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />}
                <span className="font-medium truncate">{t.title}</span>
              </div>
              <div className="text-xs text-stone-500 mt-0.5 flex items-center gap-2 flex-wrap">
                <span>{t.author_name}</span>
                <span>· {t.category_name}</span>
                <span>· {formatRel(t.last_activity)}</span>
              </div>
            </div>
            <div className="text-right text-xs text-stone-500 shrink-0 hidden sm:block">
              <div className="font-medium text-stone-700">{t.reply_count}</div>
              <div>repl{t.reply_count === 1 ? "y" : "ies"}</div>
            </div>
            <ChevronRight className="w-4 h-4 text-stone-300 shrink-0" />
          </Link>
        </li>
      ))}
    </ul>
  );
}

// ════════════════════════════ Category view ════════════════════════════

export function ForumCategory() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [cat, setCat] = useState(null);
  const [threads, setThreads] = useState([]);

  useEffect(() => {
    pub.get("/public/forum/categories").then((r) => {
      setCat(r.data.find((c) => c.slug === slug) || null);
    });
    pub.get(`/public/forum/threads?category=${slug}`).then((r) => setThreads(r.data));
  }, [slug]);

  return (
    <ForumLayout isAuth={!!user}>
      <Link to="/forum" className="text-sm text-stone-500 hover:text-stone-900 inline-flex items-center gap-1 mb-4" data-testid="forum-back">
        <ArrowLeft className="w-3.5 h-3.5" /> All categories
      </Link>
      {cat && (
        <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-light tracking-tight">{cat.name}</h1>
            <p className="text-stone-600 mt-1">{cat.description}</p>
          </div>
          {user && (
            <Link to={`/forum/new?category=${slug}`}><Button className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="forum-new-thread-in-cat"><Plus className="w-4 h-4 mr-1.5" /> New thread</Button></Link>
          )}
        </div>
      )}
      <ThreadList threads={threads} />
    </ForumLayout>
  );
}

// ════════════════════════════ Thread detail ════════════════════════════

export function ForumThread() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [reply, setReply] = useState("");
  const [replyVideos, setReplyVideos] = useState([]);
  const [posting, setPosting] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);

  const load = () => {
    pub.get(`/public/forum/threads/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.status === 404 ? "Thread not found" : "Could not load"));
  };
  useEffect(() => { load(); }, [slug]);

  if (error) return <ForumLayout isAuth={!!user}><p data-testid="thread-error">{error}</p></ForumLayout>;
  if (!data) return <ForumLayout isAuth={!!user}><p className="text-stone-500">Loading…</p></ForumLayout>;
  const { thread, posts } = data;
  const isOwner = user && user.id === thread.author_id;
  const isAdmin = user?.is_admin;

  const postReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setPosting(true);
    try {
      await api.post(`/forum/threads/${thread.id}/posts`, { body: reply, videos: replyVideos });
      setReply(""); setReplyVideos([]); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not post"); }
    finally { setPosting(false); }
  };

  const vote = async (postId, currentScore, currentMy, val) => {
    if (!user) return navigate("/login");
    const newVal = currentMy === val ? 0 : val;
    try {
      const { data: r } = await api.post(`/forum/posts/${postId}/vote`, { value: newVal });
      setData((prev) => ({
        ...prev,
        posts: prev.posts.map((p) => p.id === postId ? { ...p, vote_score: r.vote_score, my_vote: newVal } : p),
      }));
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const markSolved = async (postId) => {
    try {
      await api.post(`/forum/threads/${thread.id}/solve`, { post_id: postId });
      toast.success("Marked as solution"); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const deletePost = async (postId) => {
    if (!window.confirm("Delete this post?")) return;
    try { await api.delete(`/forum/posts/${postId}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const togglePin = async () => {
    try {
      await api.post(`/admin/forum/threads/${thread.id}/pin`, { pinned: !thread.pinned });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const toggleLock = async () => {
    try {
      await api.post(`/admin/forum/threads/${thread.id}/lock`, { locked: !thread.locked });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <ForumLayout isAuth={!!user}>
      <Link to={`/forum/c/${thread.category_slug}`} className="text-sm text-stone-500 hover:text-stone-900 inline-flex items-center gap-1 mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> {thread.category_name || "Back"}
      </Link>

      <article className="bg-white border border-stone-200 rounded-2xl p-6" data-testid={`thread-${thread.slug}`}>
        <div className="flex items-start gap-3">
          <Avatar name={thread.author_name} url={thread.author_avatar} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              {thread.pinned && <span className="text-[10px] uppercase tracking-[0.12em] bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded-full inline-flex items-center gap-1"><Pin className="w-3 h-3" /> Pinned</span>}
              {thread.locked && <span className="text-[10px] uppercase tracking-[0.12em] bg-stone-200 text-stone-700 px-1.5 py-0.5 rounded-full inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Locked</span>}
              {thread.solved_post_id && <span className="text-[10px] uppercase tracking-[0.12em] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Solved</span>}
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-medium">{thread.title}</h1>
            <div className="text-xs text-stone-500 mt-1">by {thread.author_name} · {formatRel(thread.created_at)} · {thread.view_count} view{thread.view_count === 1 ? "" : "s"}</div>
            <div className="text-stone-700 whitespace-pre-wrap mt-4 leading-relaxed">{thread.body}</div>
            <VideoThumbList videos={thread.videos} onPick={setActiveVideo} testid="thread-videos" />
          </div>
        </div>
        {isAdmin && (
          <div className="flex gap-2 mt-4 pt-4 border-t border-stone-100">
            <Button size="sm" variant="outline" onClick={togglePin} className="rounded-full" data-testid="thread-toggle-pin">
              <Pin className="w-3.5 h-3.5 mr-1.5" /> {thread.pinned ? "Unpin" : "Pin"}
            </Button>
            <Button size="sm" variant="outline" onClick={toggleLock} className="rounded-full" data-testid="thread-toggle-lock">
              <Lock className="w-3.5 h-3.5 mr-1.5" /> {thread.locked ? "Unlock" : "Lock"}
            </Button>
          </div>
        )}
      </article>

      <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500 mt-8 mb-3">
        {posts.length} {posts.length === 1 ? "reply" : "replies"}
      </h2>

      <ul className="space-y-3" data-testid="thread-posts">
        {posts.map((p) => (
          <li key={p.id} className={`bg-white border ${thread.solved_post_id === p.id ? "border-emerald-300 bg-emerald-50/30" : "border-stone-200"} rounded-2xl p-5`} data-testid={`post-${p.id}`}>
            <div className="flex items-start gap-3">
              <div className="flex flex-col items-center gap-0.5 shrink-0">
                <button onClick={() => vote(p.id, p.vote_score, p.my_vote, 1)} className="text-stone-400 hover:text-amber-700" data-testid={`post-upvote-${p.id}`}><ArrowUp className="w-4 h-4" /></button>
                <span className="text-sm font-medium tabular-nums">{p.vote_score}</span>
                <button onClick={() => vote(p.id, p.vote_score, p.my_vote, -1)} className="text-stone-400 hover:text-stone-700" data-testid={`post-downvote-${p.id}`}><ArrowDown className="w-4 h-4" /></button>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Avatar name={p.author_name} url={p.author_avatar} size="sm" />
                  <span className="text-sm font-medium">{p.author_name}</span>
                  <span className="text-xs text-stone-400">· {formatRel(p.created_at)}{p.edited_at ? " · edited" : ""}</span>
                  {thread.solved_post_id === p.id && (
                    <span className="text-[10px] uppercase tracking-[0.12em] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full inline-flex items-center gap-1 ml-auto"><Award className="w-3 h-3" /> Solution</span>
                  )}
                </div>
                <div className="text-stone-700 whitespace-pre-wrap mt-2 leading-relaxed">{p.body}</div>
                <VideoThumbList videos={p.videos} onPick={setActiveVideo} testid={`post-videos-${p.id}`} />
                <div className="flex items-center gap-3 mt-3 text-xs">
                  {isOwner && !thread.locked && (
                    <button onClick={() => markSolved(thread.solved_post_id === p.id ? "" : p.id)} className="text-emerald-700 hover:underline inline-flex items-center gap-1" data-testid={`post-mark-solved-${p.id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5" /> {thread.solved_post_id === p.id ? "Unmark solution" : "Mark as solution"}
                    </button>
                  )}
                  {(user?.id === p.author_id || isAdmin) && (
                    <button onClick={() => deletePost(p.id)} className="text-stone-400 hover:text-red-700 inline-flex items-center gap-1" data-testid={`post-delete-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {thread.locked ? (
        <p className="text-sm text-stone-500 mt-8 text-center"><Lock className="w-4 h-4 inline mr-1" /> This thread is locked.</p>
      ) : user ? (
        <form onSubmit={postReply} className="mt-8 bg-white border border-stone-200 rounded-2xl p-5" data-testid="reply-form">
          <Label className="text-xs uppercase tracking-[0.15em] text-stone-500 font-semibold">Reply</Label>
          <Textarea rows={4} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Share what worked, what didn't…" minLength={2} maxLength={10000} className="mt-2" data-testid="reply-body" />
          <div className="mt-3">
            <VideoUploadField videos={replyVideos} onChange={setReplyVideos} testid="reply-video-field" />
          </div>
          <div className="flex justify-end mt-3">
            <Button type="submit" disabled={posting || !reply.trim()} className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="reply-submit">
              <Send className="w-4 h-4 mr-1.5" /> {posting ? "Posting…" : "Post reply"}
            </Button>
          </div>
        </form>
      ) : (
        <div className="mt-8 text-center p-6 bg-white border border-stone-200 rounded-2xl">
          <p className="text-stone-600 text-sm"><Link to="/login" className="text-amber-700 hover:underline" data-testid="reply-signin-link">Sign in</Link> or <Link to="/register" className="text-amber-700 hover:underline">create an account</Link> to reply and vote.</p>
        </div>
      )}

      {activeVideo && <VideoLightbox video={activeVideo} onClose={() => setActiveVideo(null)} />}
    </ForumLayout>
  );
}

function Avatar({ name, url, size = "md" }) {
  const cls = size === "sm" ? "w-6 h-6 text-[10px]" : "w-10 h-10 text-sm";
  if (url) return <img src={url} alt="" className={`${cls} rounded-full object-cover border border-stone-200 shrink-0`} />;
  return <div className={`${cls} rounded-full bg-amber-100 text-amber-800 grid place-items-center font-semibold shrink-0`}>{(name || "?")[0]?.toUpperCase()}</div>;
}

function VideoThumbList({ videos, onPick, testid }) {
  if (!videos?.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-3" data-testid={testid}>
      {videos.map((v, i) => (
        <button
          key={`${v.public_id}-${i}`}
          type="button"
          onClick={() => onPick(v)}
          className="relative group w-32 h-20 rounded-lg overflow-hidden border border-stone-200 bg-stone-100"
          data-testid={`${testid}-thumb-${i}`}
          aria-label="Play video"
        >
          {v.poster_url && <img src={v.poster_url} alt="" className="w-full h-full object-cover" loading="lazy" />}
          <span className="absolute inset-0 grid place-items-center bg-black/30 group-hover:bg-black/40 transition-colors">
            <svg viewBox="0 0 24 24" className="w-7 h-7 text-white drop-shadow" fill="currentColor"><polygon points="7,5 19,12 7,19" /></svg>
          </span>
          {v.duration > 0 && (
            <span className="absolute bottom-1 right-1 text-[10px] font-medium text-white bg-black/60 px-1.5 py-0.5 rounded">
              {Math.round(v.duration)}s
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

// ════════════════════════════ New thread (auth-required) ════════════════════════════

export function ForumNewThread() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [sp] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ category_slug: sp.get("category") || "general", title: "", body: "", videos: [] });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    pub.get("/public/forum/categories").then((r) => setCats(r.data));
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  if (loading) return <ForumLayout isAuth={!!user}><p className="text-stone-500">Loading…</p></ForumLayout>;
  if (!user) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/forum/threads", form);
      toast.success("Thread created");
      navigate(`/forum/t/${data.slug}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <ForumLayout isAuth={true}>
      <Link to="/forum" className="text-sm text-stone-500 hover:text-stone-900 inline-flex items-center gap-1 mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to forum
      </Link>
      <h1 className="font-display text-3xl font-light tracking-tight">Start a new thread</h1>
      <form onSubmit={submit} className="mt-6 space-y-4 max-w-2xl" data-testid="new-thread-form">
        <div>
          <Label>Category</Label>
          <Select value={form.category_slug} onValueChange={(v) => setForm({ ...form, category_slug: v })}>
            <SelectTrigger className="mt-1.5" data-testid="new-thread-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              {cats.map((c) => <SelectItem key={c.id} value={c.slug}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Title</Label>
          <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="What's the question or topic?" minLength={4} maxLength={200} className="mt-1.5" data-testid="new-thread-title" />
        </div>
        <div>
          <Label>Body</Label>
          <Textarea required rows={8} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Give us the details…" minLength={4} maxLength={10000} className="mt-1.5" data-testid="new-thread-body" />
        </div>
        <div>
          <Label>Videos</Label>
          <div className="mt-1.5">
            <VideoUploadField
              videos={form.videos}
              onChange={(videos) => setForm((prev) => ({ ...prev, videos }))}
              testid="new-thread-video-field"
            />
          </div>
        </div>
        <Button type="submit" disabled={busy} className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="new-thread-submit">
          {busy ? "Posting…" : "Post thread"}
        </Button>
      </form>
    </ForumLayout>
  );
}
